import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Card,
    Stack,
    Chip,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { PRACTICAL_EVALUATION_STORE } from '../../../config/constants';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';

export default function PracticalEvaluationList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Creation modal
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedLiftType, setSelectedLiftType] = useState('');
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [createError, setCreateError] = useState('');

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(PRACTICAL_EVALUATION_STORE)) {
                const tx = db.transaction(PRACTICAL_EVALUATION_STORE, 'readonly');
                const store = tx.objectStore(PRACTICAL_EVALUATION_STORE);
                const allForms = await store.getAll();
                const sorted = (allForms || [])
                    .filter(f => f.status !== 'submitted' && f.status !== 'send')
                    .filter(f => !f.evaluationTypeSlug || !f.evaluationTypeSlug.startsWith('forklift-'))
                    .sort((a, b) => {
                        const dateA = new Date(a.date || a.updatedAt || a.createdAt || 0);
                        const dateB = new Date(b.date || b.updatedAt || b.createdAt || 0);
                        return dateB - dateA;
                    });
                setItems(sorted);
            }
        } catch (e) {
            console.error('Error loading forms:', e);
        } finally {
            setLoading(false);
        }
    }

    const filtered = useMemo(() => items, [items]);

    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filtered.slice(startIndex, startIndex + itemsPerPage);
    }, [filtered, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const handlePageChange = (event, value) => setCurrentPage(value);
    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const handleDelete = async (uuid) => {
        if (!window.confirm('Are you sure you want to delete this evaluation?')) return;
        try {
            const db = await initializeDB();
            const tx = db.transaction(PRACTICAL_EVALUATION_STORE, 'readwrite');
            const store = tx.objectStore(PRACTICAL_EVALUATION_STORE);
            await store.delete(uuid);
            await load();
        } catch (e) {
            console.error('Error deleting form:', e);
        }
    };

    const cardTitle = (rec) => {
        const liftType = rec.checklistResponses?.liftType || '';
        const operatorName = rec.operatorName || '';
        const date = rec.date || '';
        const parts = [liftType, operatorName, date].filter(Boolean);
        if (parts.length > 0) return parts.join(' - ');
        return rec.uuid;
    };

    const handleOpenCreateModal = () => {
        setSelectedLiftType('');
        setSelectedOperator(null);
        setCreateError('');
        setCreateModalOpen(true);
    };

    const handleSelectEmployee = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) {
            setEmployeeModalOpen(false);
            return;
        }
        const person = selectedArray[0];
        setSelectedOperator({
            id: person.id || person.ID || person.employeeId || person.tempId || '',
            name: person.FullName || person.name || ''
        });
        setEmployeeModalOpen(false);
    };

    const handleCreate = () => {
        if (!selectedLiftType) {
            setCreateError('Select lift type');
            return;
        }
        if (!selectedOperator?.name) {
            setCreateError('Select operator');
            return;
        }
        setCreateModalOpen(false);
        navigate('/training-due/practical-evaluation/aerial-scissor-lift/new', {
            state: {
                operatorId: selectedOperator.id,
                operatorName: selectedOperator.name,
                liftType: selectedLiftType
            }
        });
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>MEWPS Proficiency</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateModal}
                        sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                    >
                        New Evaluation
                    </Button>
                </Box>
            </Box>

            {/* List */}
            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}><Typography>Loading...</Typography></Card>
                ) : filtered.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No evaluations found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Evaluation" to create one
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedItems.map((rec) => (
                                <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
                                                    {cardTitle(rec)}
                                                </Typography>
                                                <Chip
                                                    label={rec.status === 'submitted' ? 'Submitted' : 'Draft'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: rec.status === 'submitted' ? '#4caf50' : '#9e9e9e',
                                                        color: 'white',
                                                        height: 20
                                                    }}
                                                />
                                            </Box>
                                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                                {rec.checklistResponses?.liftType && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Type:</strong> {rec.checklistResponses.liftType}
                                                    </Typography>
                                                )}
                                                {rec.operatorName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Operator:</strong> {rec.operatorName}
                                                    </Typography>
                                                )}
                                                {rec.evaluatorName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Evaluator:</strong> {rec.evaluatorName}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                Updated: {new Date(rec.updatedAt || rec.createdAt).toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/training-due/practical-evaluation/edit?uuid=${encodeURIComponent(rec.uuid)}`)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(rec.uuid)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Card>
                            ))}
                        </Stack>

                        {filtered.length > itemsPerPage && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
                                    </Typography>
                                    <FormControl size="small">
                                        <Select value={itemsPerPage} onChange={handleItemsPerPageChange} sx={{ minWidth: 80 }}>
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={25}>25</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="medium"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Creation Modal */}
            <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>New MEWPS Evaluation</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Lift Type *</Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                            <Button
                                variant={selectedLiftType === 'Scissor Lift' ? 'contained' : 'outlined'}
                                color="primary"
                                size="large"
                                onClick={() => setSelectedLiftType('Scissor Lift')}
                                sx={{ flex: 1, py: 1.5 }}
                            >
                                Scissor Lift
                            </Button>
                            <Button
                                variant={selectedLiftType === 'Boom Lift' ? 'contained' : 'outlined'}
                                color="primary"
                                size="large"
                                onClick={() => setSelectedLiftType('Boom Lift')}
                                sx={{ flex: 1, py: 1.5 }}
                            >
                                Boom Lift
                            </Button>
                        </Stack>

                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Operator *</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={selectedOperator?.name || ''}
                            placeholder="Select operator..."
                            onClick={() => setEmployeeModalOpen(true)}
                            InputProps={{
                                readOnly: true,
                                style: { cursor: 'pointer' },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setEmployeeModalOpen(true)}>
                                            <PersonSearchIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {createError && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                {createError}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreateModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleCreate}>
                        Start Evaluation
                    </Button>
                </DialogActions>
            </Dialog>

            {employeeModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={employeeModalOpen}
                    onClose={() => setEmployeeModalOpen(false)}
                    onSelect={handleSelectEmployee}
                    title="Select Operator"
                    singleSelect={true}
                    alreadySelected={[]}
                />
            )}
        </Box>
    );
}
